/**
 * incredible-popup.js
 * Popup logic for the Incredible Popup Shopify section.
 * Upload to: theme/assets/incredible-popup.js
 */

(function () {
  'use strict';

  // ─── Storage helpers ────────────────────────────────────────────────────────

  /**
   * Record that the popup was dismissed on the current URL so it won't
   * reappear on this page for the rest of the session, even on reload.
   * @param {string} popupId
   */
  function recordDismissal(popupId) {
    try {
      var key = 'incredible_popup_' + popupId + '_dismissed';
      var dismissed = JSON.parse(sessionStorage.getItem(key) || '[]');
      var path = window.location.pathname;
      if (dismissed.indexOf(path) === -1) {
        dismissed.push(path);
        sessionStorage.setItem(key, JSON.stringify(dismissed));
      }
    } catch (e) { /* storage unavailable */ }
  }

  /**
   * Check whether the popup was already dismissed on this exact URL.
   * @param {string} popupId
   * @returns {boolean}
   */
  function wasDismissedHere(popupId) {
    try {
      var key = 'incredible_popup_' + popupId + '_dismissed';
      var dismissed = JSON.parse(sessionStorage.getItem(key) || '[]');
      return dismissed.indexOf(window.location.pathname) !== -1;
    } catch (e) { return false; }
  }

  /**
   * Decide whether the popup should be shown based on frequency settings.
   * @param {string} frequencyType  'per_session' | 'per_day' | 'always'
   * @param {number} maxDisplays    Maximum allowed displays per period
   * @param {string} popupId        Unique section ID used as storage key
   * @returns {boolean}
   */
  function shouldShowPopup(frequencyType, maxDisplays, popupId) {
    // Never re-show on a page where the user already dismissed it
    if (wasDismissedHere(popupId)) return false;

    if (frequencyType === 'always') return true;

    var key = 'incredible_popup_' + popupId;

    if (frequencyType === 'per_session') {
      try {
        var count = parseInt(sessionStorage.getItem(key) || '0', 10);
        return count < maxDisplays;
      } catch (e) {
        return true; // storage blocked — show anyway
      }
    }

    if (frequencyType === 'per_day') {
      try {
        var today = new Date().toDateString();
        var stored = JSON.parse(localStorage.getItem(key) || '{"date":"","count":0}');
        if (stored.date !== today) return true;
        return stored.count < maxDisplays;
      } catch (e) {
        return true;
      }
    }

    return true;
  }

  /**
   * Record that the popup was shown (counts toward display limit).
   * @param {string} frequencyType
   * @param {string} popupId
   */
  function recordDisplay(frequencyType, popupId) {
    if (frequencyType === 'always') return;

    var key = 'incredible_popup_' + popupId;

    try {
      if (frequencyType === 'per_session') {
        var count = parseInt(sessionStorage.getItem(key) || '0', 10);
        sessionStorage.setItem(key, String(count + 1));
      }

      if (frequencyType === 'per_day') {
        var today = new Date().toDateString();
        var stored = JSON.parse(localStorage.getItem(key) || '{"date":"","count":0}');
        var newCount = stored.date === today ? stored.count + 1 : 1;
        localStorage.setItem(key, JSON.stringify({ date: today, count: newCount }));
      }
    } catch (e) {
      // Storage unavailable — silently continue
    }
  }

  // ─── Popup visibility ────────────────────────────────────────────────────────

  var inEditor = window.Shopify && window.Shopify.designMode;

  function showPopup(overlay, moveFocus) {
    overlay.classList.add('incredible-popup--visible');
    // Don't lock page scroll inside the theme editor — it traps the sidebar
    if (!inEditor) document.body.classList.add('incredible-popup-open');
    // Only move focus on the live site — never steal it from editor inputs
    if (moveFocus) {
      var firstFocusable = overlay.querySelector('button, a, input, [tabindex]');
      if (firstFocusable) firstFocusable.focus();
    }
  }

  function hidePopup(overlay) {
    overlay.classList.remove('incredible-popup--visible');
    document.body.classList.remove('incredible-popup-open');
  }

  // ─── Trap focus inside modal while open ──────────────────────────────────────

  function trapFocus(overlay, event) {
    if (!overlay.classList.contains('incredible-popup--visible')) return;
    var focusable = Array.from(
      overlay.querySelectorAll('button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
    ).filter(function (el) { return !el.disabled; });

    if (focusable.length === 0) return;

    var first = focusable[0];
    var last  = focusable[focusable.length - 1];

    if (event.shiftKey) {
      if (document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  // ─── Init ────────────────────────────────────────────────────────────────────

  function initPopup() {
    var overlays = document.querySelectorAll('.incredible-popup[data-popup-id]');

    overlays.forEach(function (overlay) {
      var delaySeconds   = parseFloat(overlay.dataset.delay || '0');
      var frequencyType  = overlay.dataset.frequencyType  || 'per_session';
      var maxDisplays    = parseInt(overlay.dataset.maxDisplays || '1', 10);
      var popupId        = overlay.dataset.popupId;

      // ── URL exclusion — check before anything else ──
      var excludePaths = (overlay.dataset.excludePaths || '')
        .split('\n')
        .map(function (p) { return p.trim(); })
        .filter(Boolean);
      if (excludePaths.some(function (p) {
        return window.location.pathname === p || window.location.pathname.indexOf(p) === 0;
      })) return;

      // ── Theme editor: always show immediately, never record a display ──

      if (!inEditor) {
        // Frequency gate — skip for editors
        if (!shouldShowPopup(frequencyType, maxDisplays, popupId)) return;
      }

      // Show after delay (no delay in editor so you see it right away)
      var delayMs = inEditor ? 0 : Math.max(0, delaySeconds * 1000);
      setTimeout(function () {
        showPopup(overlay, !inEditor);
        if (!inEditor) recordDisplay(frequencyType, popupId);
      }, delayMs);

      // ── Close button ──
      var closeBtn = overlay.querySelector('.incredible-popup__close');
      if (closeBtn) {
        closeBtn.addEventListener('click', function () {
          hidePopup(overlay);
          if (!inEditor) recordDismissal(popupId);
        });
      }

      // ── Click on dark overlay backdrop to close ──
      overlay.addEventListener('click', function (e) {
        if (e.target === overlay) {
          hidePopup(overlay);
          if (!inEditor) recordDismissal(popupId);
        }
      });

      // ── ESC key ──
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' || e.keyCode === 27) {
          hidePopup(overlay);
          if (!inEditor) recordDismissal(popupId);
        }
        // Tab-trap
        if (e.key === 'Tab' || e.keyCode === 9) {
          trapFocus(overlay, e);
        }
      });
    });
  }

  // ─── Bootstrap ───────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPopup);
  } else {
    initPopup();
  }

})();
